import React from 'react';
import { FlexBox, Typography, Link } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { ModTarget } from '@vp/mod-father';
import { useSelector } from 'react-redux';
import { PurcsModal } from './PurcsModal';
import { cartItemSelectors } from '../state/cartItems';
import FixedRatioImage from './shared/FixedRatioImage';
import IfDebug from './debugTooling/IfDebug';
import RequireTranslations from './shared/RequireTranslations';
import useTracking from '../hooks/useTracking';
import type { CartItemsState } from '../types';

export interface DesignPreviewProps {
  cartItemId: string;
}

export const DesignPreview = ({ cartItemId }: DesignPreviewProps) => {
  const [cartMessages] = useTranslations(['cartMessages']);

  const previewUrl = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectPreviewUrlByItemId(state.cartItems, cartItemId)
  );
  const editDesignUrl = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectEditDesignUrl(state.cartItems, cartItemId)
  );
  const editOptionsUrl = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectEditOptionsUrl(state.cartItems, cartItemId)
  );
  const documentRefUrl = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectDocumentRefUrl(state.cartItems, cartItemId)
  );
  const productName = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectProductNameByItemId(state.cartItems, cartItemId)
  );
  const productKey = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectProductKeyByItemId(state.cartItems, cartItemId)
  );
  const productAttributeList = useSelector((state: CartItemsState) =>
    cartItemSelectors.selectProductAttributeList(state.cartItems, cartItemId)
  );

  const tracker = useTracking();

  const editDesignClickHandler = () => {
    tracker.productDesignEditClicked(cartItemId);
  };

  const multipleEditLinks = editDesignUrl && editOptionsUrl;
  const editMessage = (
    <RequireTranslations group="cartMessages" keys={['edit']}>
      {cartMessages.edit}
    </RequireTranslations>
  );
  let editDesignBlock = <div />;
  if (editDesignUrl) {
    const editDesignMessage = (
      <RequireTranslations group="cartMessages" keys={['editDesign']}>
        {cartMessages.editDesign}
      </RequireTranslations>
    );

    editDesignBlock = (
      <Link href={editDesignUrl} onClick={editDesignClickHandler}>
        {multipleEditLinks ? editDesignMessage : editMessage}
      </Link>
    );
  }

  const usePurcs = !!documentRefUrl;

  return (
    <>
      {usePurcs ? (
        <>
          <IfDebug>
            <Typography fontSize={1} color="dark-grey">
              PURCS preview
            </Typography>
          </IfDebug>
          <PurcsModal
            editDesignBlock={editDesignBlock}
            cartItemId={cartItemId}
            docRefUrl={documentRefUrl}
            productName={productName}
            productKey={productKey}
            productAttributeList={productAttributeList}
          />
        </>
      ) : (
        <>
          <IfDebug>
            <Typography fontSize={1} color="dark-grey">
              static preview
            </Typography>
          </IfDebug>
          <FixedRatioImage src={previewUrl} alt="" />
          <FlexBox justifyContent="center">{editDesignBlock}</FlexBox>
        </>
      )}
      <ModTarget name="editOptions">
        {editOptionsUrl ? (
          <FlexBox justifyContent="center" style={{ textAlign: 'center' }}>
            <Link href={editOptionsUrl}>
              {multipleEditLinks ? (
                <RequireTranslations
                  group="cartMessages"
                  keys={['editOptions']}
                >
                  {cartMessages.editOptions}
                </RequireTranslations>
              ) : (
                editMessage
              )}
            </Link>
          </FlexBox>
        ) : null}
      </ModTarget>
    </>
  );
};
